import { Document, Page } from "react-pdf";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "@/modules/file/pdf-worker-setup";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { clamp } from "lodash-es";
import { MDSModal } from "@/mds/components/overlays/modal/modal";
import { MDSLoadingSpinner } from "@/mds/components/loading/loading-spinner";
import {
  MDSButtonSize,
  MDSButtonVariant,
  MDSIconButton,
  MDSIconButtonSize,
  MDSIconButtonVariant,
} from "@/mds/components/buttons";
import { MDSIconColor, MDSIconKind } from "@/mds/components/icon";
import { MDSText, MDSTextColor, MDSTextVariant, MDSTextWeight } from "@/mds/components/text";
import { MDSTooltip, MDSTooltipPlacement } from "@/mds/components/tooltip";
import { IPdfLightboxProps } from "./pdf-lightbox.types";
import { usePdfDocument } from "./use-pdf-document";
import {
  StyledPdfContentArea,
  StyledPdfScrollContainer,
  StyledPdfZoomWrapper,
  StyledPdfLightboxContainer,
  StyledOverlayLayer,
  StyledPdfToolbar,
  StyledPdfCarouselWrapper,
  StyledPageIndicator,
  StyledPageIndicatorButton,
  StyledPageIndicatorValue,
  StyledPageInput,
  StyledPageCurrent,
  StyledPageSeparator,
  StyledPageTotal,
  StyledZoomResetButton,
  StyledPdfLoadingState,
  StyledPdfErrorState,
} from "./pdf-lightbox.styles";
import { PdfPageCarousel } from "./pdf-page-carousel";
import { getShortcutTooltipConfig } from "@/modules/keyboard-shortcuts/tooltip";
import { ActionToolbarGroup } from "../action-toolbar";
import { downloadImageFromUrl } from "../media-download";
import { ZOOM_STEP, MIN_ZOOM, MAX_ZOOM, DEFAULT_ZOOM, ZOOM_EPSILON } from "../lightbox.constants";
import { PdfFindStore } from "./pdf-find-store";
import { PdfFindBar } from "./pdf-find-bar";
import { useOverlayAutoHide } from "./use-overlay-auto-hide";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { trackEvent, TrackedEvent } from "@/domains/metrics";
import { isEditableElement } from "@/modules/html";
import type { PdfViewerPageChangeMethod } from "@/domains/metrics/tracked-event";

const DEVICE_PIXEL_RATIO = Math.min(2, typeof window !== "undefined" ? window.devicePixelRatio : 1);

const hasModifierKey = (event: KeyboardEvent) => event.ctrlKey || event.metaKey || event.altKey;

const stopHandledKeyboardEvent = (event: KeyboardEvent) => {
  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation?.();
};

export const PdfLightbox = observer(
  ({
    src,
    fileName,
    initialPage = 1,
    isOpen,
    isDismissable = true,
    handleClose,
    onOpenChange,
    zIndex,
    context,
  }: IPdfLightboxProps) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [userZoom, setUserZoom] = useState(DEFAULT_ZOOM);
    const [showCarousel, setShowCarousel] = useState(false);
    const [pageDimensions, setPageDimensions] = useState<{ width: number; height: number } | null>(
      null
    );
    const [containerSize, setContainerSize] = useState<{ width: number; height: number } | null>(
      null
    );
    const lightboxContainerRef = useRef<HTMLDivElement | null>(null);
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);

    const {
      numPages,
      isLoaded,
      loadedSrc,
      pdfDocument,
      onDocumentLoadSuccess,
      onDocumentLoadError,
    } = usePdfDocument(src);
    const isCurrentDocumentLoaded = isLoaded && loadedSrc === src;

    // Analytics: session tracking
    const pagesViewedRef = useRef(new Set<number>());
    const sessionStartRef = useRef<number>(0);

    const trackPageChange = useCallback(
      (page: number, method: PdfViewerPageChangeMethod) => {
        pagesViewedRef.current.add(page);
        if (context) {
          trackEvent(TrackedEvent.PdfViewerPageChange, {
            context,
            page,
            total_pages: numPages,
            method,
          });
        }
      },
      [numPages, context]
    );
    const previousSrcRef = useRef(src);

    const findStore = useMemo(() => new PdfFindStore(), []);

    const { isOverlayVisible, handleOverlayMouseEnter, handleOverlayMouseLeave } =
      useOverlayAutoHide({
        isOpen: !!isOpen,
        disableAutoHide: findStore.isOpen,
      });

    // Dispose find store on unmount
    useEffect(() => {
      return () => findStore.dispose();
    }, [findStore]);

    // Initialize find controller when PDF document loads
    useEffect(() => {
      if (pdfDocument && loadedSrc === src) {
        findStore.initController(pdfDocument);
      }
    }, [pdfDocument, findStore, loadedSrc, src]);

    // Navigate to selected page when find highlights a match on a different page
    useEffect(() => {
      if (!findStore.query.trim()) {
        return;
      }

      if (findStore.selectedPageIndex >= 0) {
        const targetPage = findStore.selectedPageIndex + 1; // pageIdx is 0-based
        setCurrentPage(targetPage);
      }
    }, [findStore.query, findStore.selectedPageIndex]);

    // Observe scroll container size so we can compute fit-to-viewport zoom.
    // This fires on window resize and when the carousel toggles (changing flex layout).
    useEffect(() => {
      const el = scrollContainerRef.current;
      if (!el || !isOpen) return;

      const observer = new ResizeObserver(entries => {
        const entry = entries[0];
        if (entry) {
          setContainerSize({
            width: entry.contentRect.width,
            height: entry.contentRect.height,
          });
        }
      });

      observer.observe(el);
      return () => observer.disconnect();
    }, [isOpen]);

    // Compute the base zoom that fits the PDF page within the container.
    const baseZoom =
      pageDimensions && containerSize
        ? Math.min(
            containerSize.width / pageDimensions.width,
            containerSize.height / pageDimensions.height
          )
        : 1;

    const effectiveZoom = baseZoom * userZoom;

    const isZoomed = userZoom > DEFAULT_ZOOM + ZOOM_EPSILON;

    // Reset state when modal opens or a new source is displayed.
    useEffect(() => {
      if (isOpen) {
        const resolvedInitialPage = Math.max(1, Math.floor(initialPage));
        setCurrentPage(resolvedInitialPage);
        setUserZoom(DEFAULT_ZOOM);
        setShowCarousel(false);
        setPageDimensions(null);
        setContainerSize(null);
        setIsPageInputVisible(false);
        setPageInputValue("");
        findStore.reset();
        requestAnimationFrame(() => {
          // focus the container for the sake of useHotkey
          lightboxContainerRef.current?.focus();
        });
        if (previousSrcRef.current !== src) {
          previousSrcRef.current = src;
        }

        // Analytics: track open and start session
        pagesViewedRef.current = new Set([resolvedInitialPage]);
        sessionStartRef.current = Date.now();
        if (context) {
          trackEvent(TrackedEvent.PdfViewerOpen, { context });
        }
      }
    }, [isOpen, initialPage, findStore, src, context]);

    // Analytics: track close when lightbox is dismissed
    const prevIsOpenRef = useRef(false);
    const contextRef = useRef(context);
    contextRef.current = context;
    useEffect(() => {
      if (prevIsOpenRef.current && !isOpen && contextRef.current) {
        trackEvent(TrackedEvent.PdfViewerClose, {
          context: contextRef.current,
          pages_viewed: pagesViewedRef.current.size,
          total_pages: numPages,
          session_duration_ms: Date.now() - sessionStartRef.current,
        });
      }
      prevIsOpenRef.current = !!isOpen;
    }, [isOpen, numPages]);

    useEffect(() => {
      if (!isOpen || !isCurrentDocumentLoaded || !numPages) return;

      setCurrentPage(prev => clamp(prev, 1, numPages));
    }, [isCurrentDocumentLoaded, isOpen, numPages]);

    const handleZoomIn = useCallback(() => {
      setUserZoom(prev => clamp(prev + ZOOM_STEP, MIN_ZOOM, MAX_ZOOM));
    }, []);

    const handleZoomOut = useCallback(() => {
      setUserZoom(prev => clamp(prev - ZOOM_STEP, MIN_ZOOM, MAX_ZOOM));
    }, []);

    const handleZoomReset = useCallback(() => {
      setUserZoom(DEFAULT_ZOOM);
    }, []);

    const goToNextPage = useCallback(
      (method: PdfViewerPageChangeMethod) => {
        if (!isCurrentDocumentLoaded || !numPages) return;
        setCurrentPage(prev => {
          const next = Math.min(prev + 1, numPages);
          if (next !== prev) trackPageChange(next, method);
          return next;
        });
      },
      [isCurrentDocumentLoaded, numPages, trackPageChange]
    );

    const goToPreviousPage = useCallback(
      (method: PdfViewerPageChangeMethod) => {
        if (!isCurrentDocumentLoaded || !numPages) return;
        setCurrentPage(prev => {
          const next = Math.max(prev - 1, 1);
          if (next !== prev) trackPageChange(next, method);
          return next;
        });
      },
      [isCurrentDocumentLoaded, numPages, trackPageChange]
    );

    const handleNextPage = useCallback(() => goToNextPage("arrow_button"), [goToNextPage]);
    const handlePreviousPage = useCallback(
      () => goToPreviousPage("arrow_button"),
      [goToPreviousPage]
    );

    const handlePageSelect = useCallback(
      (page: number) => {
        setCurrentPage(prev => {
          if (page !== prev) trackPageChange(page, "carousel");
          return page;
        });
      },
      [trackPageChange]
    );

    const handleDocumentLoadFailure = useCallback(
      (error: unknown) => {
        onDocumentLoadError();
        logger.error({
          message: "[PdfLightbox] load error:",
          info: { error: objectModule.safeErrorAsJson(error as Error) },
        });
      },
      [onDocumentLoadError]
    );

    // --- Page input state ---
    const [isPageInputVisible, setIsPageInputVisible] = useState(false);
    const [pageInputValue, setPageInputValue] = useState("");
    const pageInputRef = useRef<HTMLInputElement | null>(null);
    const isPageNavigationEditable = isCurrentDocumentLoaded && numPages > 1;
    const pageNumberWidthCh = Math.max(2, String(numPages || currentPage).length);
    const pageIndicatorStyle: React.CSSProperties = {
      "--pdf-lightbox-page-number-width": `${pageNumberWidthCh}ch`,
    } as React.CSSProperties;

    const closePageInput = useCallback(() => {
      setIsPageInputVisible(false);
      setPageInputValue("");
    }, []);

    const submitPageInput = useCallback(
      (raw: string) => {
        if (!numPages) {
          closePageInput();
          return;
        }

        const parsed = parseInt(raw, 10);
        if (isNaN(parsed)) {
          closePageInput();
          return;
        }

        const clamped = clamp(parsed, 1, numPages);
        setCurrentPage(prev => {
          if (clamped !== prev) trackPageChange(clamped, "page_input");
          return clamped;
        });
        closePageInput();
      },
      [closePageInput, numPages, trackPageChange]
    );

    const openPageInput = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        if (!isPageNavigationEditable) {
          return;
        }

        setPageInputValue(String(currentPage));
        setIsPageInputVisible(true);
      },
      [currentPage, isPageNavigationEditable]
    );

    const handlePageInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      // Only allow digits
      const digits = e.target.value.replace(/\D/g, "");
      setPageInputValue(digits);
    }, []);

    const handlePageInputKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        // Prevent lightbox keyboard shortcuts from firing while typing in the input.
        e.stopPropagation();
        if (e.key === "Enter") {
          e.preventDefault();
          submitPageInput(pageInputValue);
        } else if (e.key === "Escape") {
          e.preventDefault();
          closePageInput();
        }
      },
      [closePageInput, pageInputValue, submitPageInput]
    );

    const handlePageInputBlur = useCallback(() => {
      closePageInput();
    }, [closePageInput]);

    const handlePageInputFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
      e.target.select();
    }, []);

    useEffect(() => {
      if (isPageInputVisible) {
        pageInputRef.current?.focus();
        pageInputRef.current?.select();
      }
    }, [isPageInputVisible]);

    useEffect(() => {
      if (!isOpen || findStore.isOpen || isPageInputVisible) return;

      requestAnimationFrame(() => {
        // focus the container for the sake of useHotkey
        lightboxContainerRef.current?.focus();
      });
    }, [findStore.isOpen, isOpen, isPageInputVisible]);

    const handleDownload = useCallback(async () => {
      await downloadImageFromUrl({
        src,
        fileName: fileName || "document.pdf",
      });
    }, [src, fileName]);

    /** Capture the page's intrinsic PDF dimensions (independent of current zoom/render size). */
    const handlePageLoadSuccess = useCallback(
      (page: { originalWidth: number; originalHeight: number }) => {
        setPageDimensions({ width: page.originalWidth, height: page.originalHeight });
      },
      []
    );

    const handleToggleCarousel = useCallback(() => {
      setShowCarousel(prev => !prev);
    }, []);

    // Scroll-to-navigate: when at default zoom, mouse wheel navigates pages.
    // A cooldown prevents rapid page flipping from momentum/trackpad scrolling.
    const scrollCooldownRef = useRef(false);

    const handleScrollNavigate = useCallback(
      (event: WheelEvent) => {
        if (isZoomed || !isCurrentDocumentLoaded) return;
        if (scrollCooldownRef.current) return;
        if (Math.abs(event.deltaY) < 1) return;

        event.preventDefault();

        scrollCooldownRef.current = true;
        setTimeout(() => {
          scrollCooldownRef.current = false;
        }, 300);

        if (event.deltaY > 0) {
          goToNextPage("scroll");
        } else {
          goToPreviousPage("scroll");
        }
      },
      [isCurrentDocumentLoaded, isZoomed, goToNextPage, goToPreviousPage]
    );

    useEffect(() => {
      const el = scrollContainerRef.current;
      if (!el || !isOpen) return;

      el.addEventListener("wheel", handleScrollNavigate, { passive: false });
      return () => el.removeEventListener("wheel", handleScrollNavigate);
    }, [isOpen, handleScrollNavigate]);

    const handleContainerClick = useCallback(() => {
      if (!isDismissable || !handleClose) return;
      handleClose();
    }, [handleClose, isDismissable]);

    const handleContentClick = useCallback((event: React.MouseEvent) => {
      // Only keep the lightbox open when the user clicks directly on the PDF
      // canvas or the text layer rendered on top of it (used for find-in-page).
      // Everything else in the content area (scroll container background, the
      // react-pdf__Page wrapper, the zoom wrapper, etc.) should dismiss.
      const target = event.target as HTMLElement;
      if (target.tagName === "CANVAS" || target.closest(".textLayer")) {
        event.stopPropagation();
        return;
      }
      // Let the click bubble up to handleContainerClick which will close.
    }, []);

    /** Prevent clicks on any overlay child (toolbar, carousel, find bar) from closing the lightbox. */
    const handleOverlayClick = useCallback((event: React.MouseEvent) => {
      event.stopPropagation();
    }, []);

    const handleToolbarClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
    }, []);

    const handleToolbarWheel = useCallback((event: React.WheelEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
    }, []);

    const isPageInputHotkeyEvent = (event: KeyboardEvent) => event.target === pageInputRef.current;

    useEffect(() => {
      if (!isOpen) return;

      const handleWindowKeyDown = (event: KeyboardEvent) => {
        if (event.key === "Escape") {
          if (isPageInputHotkeyEvent(event)) {
            return;
          }

          stopHandledKeyboardEvent(event);
          if (findStore.isOpen) {
            findStore.close();
            return;
          }

          if (!isDismissable || !handleClose) {
            return;
          }

          handleClose();
          return;
        }

        if ((event.metaKey || event.ctrlKey) && !event.altKey && event.key.toLowerCase() === "f") {
          stopHandledKeyboardEvent(event);
          findStore.open();
          return;
        }

        if (isEditableElement(event.target) || isEditableElement(document.activeElement)) {
          return;
        }

        if (!hasModifierKey(event) && (event.key === "ArrowLeft" || event.key === "ArrowRight")) {
          stopHandledKeyboardEvent(event);
          if (event.key === "ArrowLeft") {
            goToPreviousPage("keyboard");
          } else {
            goToNextPage("keyboard");
          }
          return;
        }

        if (event.altKey) {
          return;
        }

        if (event.key === "=" || event.key === "+" || event.code === "NumpadAdd") {
          stopHandledKeyboardEvent(event);
          handleZoomIn();
          return;
        }

        if (event.key === "-" || event.key === "_" || event.code === "NumpadSubtract") {
          stopHandledKeyboardEvent(event);
          handleZoomOut();
        }
      };

      window.addEventListener("keydown", handleWindowKeyDown, { capture: true });
      return () => window.removeEventListener("keydown", handleWindowKeyDown, { capture: true });
    }, [
      findStore,
      goToNextPage,
      goToPreviousPage,
      handleClose,
      handleZoomIn,
      handleZoomOut,
      isDismissable,
      isOpen,
    ]);

    if (!isOpen) return null;

    const zoomPercent = `${Math.round(userZoom * 100)}%`;

    const toolbarGroups: ActionToolbarGroup[] = [
      {
        key: "zoom-controls",
        items: [
          {
            key: "zoom-out",
            node: (
              <MDSTooltip
                config={getShortcutTooltipConfig({
                  action: "viewer_zoom_out",
                  label: "Zoom out",
                  placement: MDSTooltipPlacement.Top,
                })}
              >
                <MDSIconButton
                  aria-label="Zoom out"
                  iconKind={MDSIconKind.Minus}
                  variant={MDSIconButtonVariant.Default}
                  size={MDSIconButtonSize.Small}
                  isDisabled={userZoom <= MIN_ZOOM}
                  onClick={handleZoomOut}
                />
              </MDSTooltip>
            ),
          },
          {
            key: "zoom-label",
            node: (
              <MDSTooltip
                config={{
                  label: "Reset zoom",
                  placement: MDSTooltipPlacement.Top,
                }}
              >
                <StyledZoomResetButton
                  aria-label="Reset zoom"
                  variant={MDSButtonVariant.Transparent}
                  size={MDSButtonSize.Small}
                  onPress={handleZoomReset}
                >
                  {zoomPercent}
                </StyledZoomResetButton>
              </MDSTooltip>
            ),
          },
          {
            key: "zoom-in",
            node: (
              <MDSTooltip
                config={getShortcutTooltipConfig({
                  action: "viewer_zoom_in",
                  label: "Zoom in",
                  placement: MDSTooltipPlacement.Top,
                })}
              >
                <MDSIconButton
                  aria-label="Zoom in"
                  iconKind={MDSIconKind.Plus}
                  variant={MDSIconButtonVariant.Default}
                  size={MDSIconButtonSize.Small}
                  isDisabled={userZoom >= MAX_ZOOM}
                  onClick={handleZoomIn}
                />
              </MDSTooltip>
            ),
          },
        ],
      },
      {
        key: "page-navigation",
        items: [
          {
            key: "previous-page",
            node: (
              <MDSTooltip
                config={getShortcutTooltipConfig({
                  action: "viewer_previous",
                  label: "Previous page",
                  placement: MDSTooltipPlacement.Top,
                })}
              >
                <MDSIconButton
                  aria-label="Previous page"
                  iconKind={MDSIconKind.LeftArrow}
                  variant={MDSIconButtonVariant.Default}
                  size={MDSIconButtonSize.Small}
                  isDisabled={currentPage <= 1}
                  onClick={handlePreviousPage}
                />
              </MDSTooltip>
            ),
          },
          {
            key: "page-indicator",
            node: (
              <StyledPageIndicator>
                <MDSText
                  variant={MDSTextVariant.ParagraphSm}
                  weight={MDSTextWeight.Medium}
                  color={MDSTextColor.Contrast}
                >
                  {isPageInputVisible ? (
                    <StyledPageIndicatorValue style={pageIndicatorStyle}>
                      <StyledPageInput
                        ref={pageInputRef}
                        aria-label="Current page"
                        type="number"
                        min={1}
                        max={numPages}
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={pageInputValue}
                        onChange={handlePageInputChange}
                        onKeyDown={handlePageInputKeyDown}
                        onBlur={handlePageInputBlur}
                        onFocus={handlePageInputFocus}
                      />
                      <StyledPageSeparator>/</StyledPageSeparator>
                      <StyledPageTotal>{isCurrentDocumentLoaded ? numPages : "--"}</StyledPageTotal>
                    </StyledPageIndicatorValue>
                  ) : (
                    <StyledPageIndicatorButton
                      type="button"
                      disabled={!isPageNavigationEditable}
                      onClick={openPageInput}
                      aria-label="Go to page"
                    >
                      <StyledPageIndicatorValue
                        style={pageIndicatorStyle}
                        data-test-id="pdf-page-indicator-value"
                      >
                        <StyledPageCurrent data-test-id="pdf-page-current">
                          {isCurrentDocumentLoaded ? currentPage : "--"}
                        </StyledPageCurrent>
                        <StyledPageSeparator>/</StyledPageSeparator>
                        <StyledPageTotal data-test-id="pdf-page-total">
                          {isCurrentDocumentLoaded ? numPages : "--"}
                        </StyledPageTotal>
                      </StyledPageIndicatorValue>
                    </StyledPageIndicatorButton>
                  )}
                </MDSText>
              </StyledPageIndicator>
            ),
          },
          {
            key: "next-page",
            node: (
              <MDSTooltip
                config={getShortcutTooltipConfig({
                  action: "viewer_next",
                  label: "Next page",
                  placement: MDSTooltipPlacement.Top,
                })}
              >
                <MDSIconButton
                  aria-label="Next page"
                  iconKind={MDSIconKind.RightArrow}
                  variant={MDSIconButtonVariant.Default}
                  size={MDSIconButtonSize.Small}
                  isDisabled={currentPage >= numPages}
                  onClick={handleNextPage}
                />
              </MDSTooltip>
            ),
          },
        ],
      },
      {
        key: "download",
        items: [
          {
            key: "download-pdf",
            node: (
              <MDSTooltip
                config={{
                  label: "Download file",
                  placement: MDSTooltipPlacement.Top,
                }}
              >
                <MDSIconButton
                  aria-label="Download"
                  iconKind={MDSIconKind.ArrowDownToLine}
                  variant={MDSIconButtonVariant.Default}
                  size={MDSIconButtonSize.Small}
                  onClick={handleDownload}
                />
              </MDSTooltip>
            ),
          },
        ],
      },
      {
        key: "carousel",
        items: [
          {
            key: "toggle-carousel",
            node: (
              <MDSTooltip
                config={{
                  label: "Toggle thumbnails",
                  placement: MDSTooltipPlacement.Top,
                }}
              >
                <MDSIconButton
                  aria-label="Toggle carousel"
                  iconKind={MDSIconKind.GalleryThumbnails}
                  variant={MDSIconButtonVariant.Default}
                  size={MDSIconButtonSize.Small}
                  isToggled={showCarousel}
                  onClick={handleToggleCarousel}
                />
              </MDSTooltip>
            ),
          },
        ],
      },
      {
        key: "close",
        items: [
          {
            key: "close-pdf",
            node: (
              <MDSTooltip
                config={getShortcutTooltipConfig({
                  action: "escape_to_close",
                  label: "Close",
                  placement: MDSTooltipPlacement.Top,
                })}
              >
                <MDSIconButton
                  aria-label="Close"
                  iconKind={MDSIconKind.Exit}
                  variant={MDSIconButtonVariant.Default}
                  size={MDSIconButtonSize.Small}
                  onClick={handleClose}
                />
              </MDSTooltip>
            ),
          },
        ],
      },
    ];

    return (
      <MDSModal
        isOpen={isOpen}
        isDismissable={isDismissable}
        handleClose={handleClose}
        onOpenChange={onOpenChange}
        zIndex={zIndex}
      >
        <StyledPdfLightboxContainer
          ref={lightboxContainerRef}
          tabIndex={-1}
          onClick={handleContainerClick}
        >
          <StyledPdfContentArea onClick={handleContentClick}>
            <StyledPdfScrollContainer
              ref={scrollContainerRef}
              isZoomed={isZoomed}
              data-test-id="pdf-scroll-container"
            >
              <Document
                key={src}
                file={src}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={handleDocumentLoadFailure}
                onSourceError={handleDocumentLoadFailure}
                loading={
                  <StyledPdfLoadingState data-test-id="pdf-loading-state">
                    <MDSLoadingSpinner size={32} color={MDSIconColor.Inherit} />
                  </StyledPdfLoadingState>
                }
                error={
                  <StyledPdfErrorState data-test-id="pdf-error-state">
                    Failed to load PDF file
                  </StyledPdfErrorState>
                }
              >
                <StyledPdfZoomWrapper
                  zoom={effectiveZoom}
                  pageWidth={pageDimensions?.width ?? 0}
                  pageHeight={pageDimensions?.height ?? 0}
                  data-test-id="pdf-zoom-wrapper"
                >
                  <div>
                    <Page
                      pageNumber={currentPage}
                      devicePixelRatio={DEVICE_PIXEL_RATIO}
                      renderTextLayer={findStore.isOpen}
                      renderAnnotationLayer={false}
                      onLoadSuccess={handlePageLoadSuccess}
                      loading={null}
                      customTextRenderer={findStore.customTextRenderer}
                    />
                  </div>
                </StyledPdfZoomWrapper>
              </Document>
            </StyledPdfScrollContainer>
          </StyledPdfContentArea>

          <StyledOverlayLayer
            isVisible={isOverlayVisible}
            onClick={handleOverlayClick}
            onMouseEnter={handleOverlayMouseEnter}
            onMouseLeave={handleOverlayMouseLeave}
          >
            <PdfFindBar store={findStore} />

            {isCurrentDocumentLoaded && (
              <StyledPdfCarouselWrapper>
                <PdfPageCarousel
                  numPages={numPages}
                  currentPage={currentPage}
                  onPageSelect={handlePageSelect}
                  pdfSrc={src}
                  isVisible={showCarousel}
                />
              </StyledPdfCarouselWrapper>
            )}

            <StyledPdfToolbar
              groups={toolbarGroups}
              onClick={handleToolbarClick}
              onWheel={handleToolbarWheel}
            />
          </StyledOverlayLayer>
        </StyledPdfLightboxContainer>
      </MDSModal>
    );
  }
);
